import React from "react"
import { Sparkles } from "lucide-react"

import {
	UTM_KEYS,
	CLIENT_SIGNAL_FIELDS,
	DATE_SIGNAL_FIELDS,
	GROUP_SIGNAL_FIELDS,
} from "../registry"

/**
 * Shared parsed-result render. Used both by the Development tab (to show the
 * latest preview run) and by the Enquiry Queue parse modal (to show whatever
 * is saved on the queue document).
 *
 * Accepts the same `result` envelope returned by `runEmailAiTool`
 * (`{ output_text, parsed, model_used, usage, ... }`) plus the active field
 * and prediction definitions used to label the rows.
 */
export function ResultDisplay({ result, fields, predictions }) {
	const { output_text, parsed, model_used, usage } = result || {}

	return (
		<section className="ai-panel is-open">
			<div className="ai-panel-toggle">
				<Sparkles size={14} />
				<span>Parsed result</span>
			</div>

			<div className="ai-panel-body">
				<div className="ai-panel-result-meta">
					<small>
						<strong>Model:</strong> {model_used || "—"}
					</small>
					{usage && (
						<small>
							<strong>Tokens:</strong> {usage.input_tokens || 0} in /{" "}
							{usage.output_tokens || 0} out
						</small>
					)}
				</div>

				{parsed && typeof parsed === "object" ? (
					<>
						<div className="ingest-result-fields">
							{fields.map((field) => {
								const value = parsed[field.key]
								return (
									<div
										key={field.key}
										className="ingest-result-field">
										<div className="ingest-result-field-label">
											<strong>{field.label || field.key}</strong>
											<small>
												{field.key} · {field.type}
												{field.required ? " · required" : ""}
											</small>
										</div>
										<div className="ingest-result-field-value">
											{formatValue(value)}
										</div>
									</div>
								)
							})}
						</div>

						{parsed.utm_tracking && (
							<SignalBlock
								title="UTM tracking"
								signals={UTM_KEYS}
								values={parsed.utm_tracking}
							/>
						)}

						<SignalBlock
							title="Client signals"
							signals={CLIENT_SIGNAL_FIELDS}
							values={parsed.client_signals}
						/>

						<SignalBlock
							title="Date signals"
							signals={DATE_SIGNAL_FIELDS}
							values={parsed.date_signals}
						/>

						<SignalBlock
							title="Group signals"
							signals={GROUP_SIGNAL_FIELDS}
							values={parsed.group_signals}
						/>

						{predictions?.length > 0 && parsed.predictions && (
							<div className="ingest-result-predictions">
								<h4>Predictions</h4>
								{predictions.map((prediction) => {
									const score = parsed.predictions?.[prediction.key]
									return (
										<PredictionResultBar
											key={prediction.key}
											prediction={prediction}
											score={score}
										/>
									)
								})}
							</div>
						)}

						<details className="ingest-result-raw">
							<summary>Raw JSON</summary>
							<pre className="ai-panel-result-json">
								{JSON.stringify(parsed, null, 2)}
							</pre>
						</details>
					</>
				) : (
					<pre className="ai-panel-result-text">{output_text || "(empty response)"}</pre>
				)}
			</div>
		</section>
	)
}

/**
 * Render block for a fixed-shape signal object (UTM / client / date / group).
 * Skips entirely when the model didn't return the object.
 */
export function SignalBlock({ title, signals, values }) {
	if (!values) return null

	return (
		<div className="ingest-result-utm">
			<h4>{title}</h4>
			<div className="ingest-result-utm-grid">
				{signals.map((signal) => (
					<div
						key={signal.key}
						className="ingest-result-utm-row">
						<div className="ingest-result-utm-label">
							<strong>{signal.label}</strong>
							<small>{signal.key}</small>
						</div>
						<div className="ingest-result-utm-value">
							{formatValue(values?.[signal.key])}
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

export function PredictionResultBar({ prediction, score }) {
	const numericScore = typeof score === "number" && Number.isFinite(score) ? score : null
	const clamped = numericScore === null ? 0 : Math.max(0, Math.min(1, numericScore))
	const percent = Math.round(clamped * 100)

	// Colour band reflects magnitude only — semantic direction (high spam =
	// "bad") isn't encoded here; that's a downstream concern.
	let bandClass = "low"
	if (clamped >= 0.66) bandClass = "high"
	else if (clamped >= 0.33) bandClass = "mid"

	return (
		<div className="ingest-prediction-bar">
			<div className="ingest-prediction-bar-header">
				<strong>{prediction.label || prediction.key}</strong>
				<span className="ingest-prediction-bar-score">
					{numericScore === null ? "—" : numericScore.toFixed(2)}
				</span>
			</div>
			<div className="ingest-prediction-bar-track">
				<div
					className={`ingest-prediction-bar-fill ${bandClass}`}
					style={{ width: `${percent}%` }}
				/>
			</div>
			<small className="ingest-prediction-bar-key">{prediction.key}</small>
		</div>
	)
}

export const formatValue = (value) => {
	if (value === null || value === undefined) {
		return <span className="ingest-result-null">—</span>
	}
	if (Array.isArray(value)) {
		if (value.length === 0) return <span className="ingest-result-null">(empty)</span>
		return value.join(", ")
	}
	if (typeof value === "boolean") return value ? "true" : "false"
	return String(value)
}
